/**
 * Financial Module Page (Redesigned - Korat Health Style)
 *
 * Budget allocation and expense tracking with category breakdown
 */

import { useState } from 'react'
import { Wallet, TrendingUp, TrendingDown, PieChart, DollarSign } from 'lucide-react'
import {
  Sidebar,
  PageHeader,
  KpiCardRich,
  DataTable,
  ProgressBar,
  YearFilter,
  DistrictFilter,
  ExportButton,
  type Column,
} from '../shared/components'
import { LineChartCard } from '../shared/components/charts'
import {
  useFinancialOverview,
  useFinancialByDistrict,
  useFinancialTrends,
  useDistricts,
} from '../services/api'
import type { FinancialCategory } from '../types/modules'
import { formatNumber } from '../shared/utils/formatNumber'

export function FinancialPage() {
  const [selectedYear, setSelectedYear] = useState(2567)
  const [selectedDistrict, setSelectedDistrict] = useState<number | null>(null)

  const overviewQuery = useFinancialOverview(selectedYear)
  const districtQuery = useFinancialByDistrict(selectedDistrict || 0, selectedYear)

  const data = selectedDistrict ? districtQuery.data : overviewQuery.data
  const isLoading = selectedDistrict ? districtQuery.isLoading : overviewQuery.isLoading
  const error = selectedDistrict ? districtQuery.error : overviewQuery.error

  const { data: districts = [], isLoading: districtsLoading } = useDistricts()

  // Fetch 5-year trends
  const { data: trendsData, isLoading: trendsLoading } = useFinancialTrends({
    districtId: selectedDistrict || undefined,
  })

  const incomeSummary = data?.summary.find((s) => s.category_type === 'income')
  const expenseSummary = data?.summary.find((s) => s.category_type === 'expense')

  const incomeCategories = data?.by_category.filter((c) => c.category_type === 'income') || []
  const expenseCategories = data?.by_category.filter((c) => c.category_type === 'expense') || []

  const totalBudget = expenseSummary ? parseFloat(expenseSummary.total_budget) : 0
  const totalSpent = expenseSummary ? parseFloat(expenseSummary.total_spent) : 0
  const remaining = totalBudget - totalSpent
  const utilization = expenseSummary ? parseFloat(expenseSummary.avg_utilization) : 0

  const columns: Column<FinancialCategory>[] = [
    {
      header: 'หมวดหมู่',
      accessor: 'name_th',
      width: 'w-1/3',
      render: (value, row) => (
        <div>
          <div className="font-semibold text-korat-navy-700 tabular-nums">{value as string}</div>
          <div className="text-xs text-slate-500">{row.name_en}</div>
        </div>
      ),
    },
    {
      header: 'งบประมาณ',
      accessor: 'budget_allocated',
      align: 'right',
      render: (value) => (
        <span className="text-korat-navy-700 tabular-nums">{formatNumber(value as string)}</span>
      ),
    },
    {
      header: 'ใช้จ่ายจริง',
      accessor: 'actual_spent',
      align: 'right',
      render: (value) => (
        <span className="tabular-nums">{formatNumber(value as string)}</span>
      ),
    },
    {
      header: 'อัตราการใช้',
      accessor: 'utilization_rate',
      align: 'right',
      width: 'w-40',
      render: (value) => (
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <ProgressBar
              value={parseFloat(value as string)}
              showValue={false}
              variant={
                parseFloat(value as string) >= 90
                  ? 'success'
                  : parseFloat(value as string) >= 70
                  ? 'warning'
                  : 'danger'
              }
            />
          </div>
          <span className="text-sm font-semibold w-12 text-right tabular-nums">
            {parseFloat(value as string).toFixed(1)}%
          </span>
        </div>
      ),
    },
  ]

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar currentPage="financial" />

      <main className="flex-1 overflow-auto">
        <PageHeader
          icon={Wallet}
          title="งบประมาณและการเงิน"
          showFilters={true}
          selectedDistrict={
            selectedDistrict ? districts.find((d) => d.id === selectedDistrict)?.name_th : null
          }
          filters={
            <>
              <YearFilter selectedYear={selectedYear} onYearChange={setSelectedYear} />
              <DistrictFilter
                selectedDistrict={selectedDistrict}
                onDistrictChange={setSelectedDistrict}
                districts={districts}
                isLoading={districtsLoading}
              />
              <ExportButton module="financial" year={selectedYear} districtId={selectedDistrict} />
            </>
          }
        />

        <div className="p-6 space-y-6">
          {/* KPI Cards */}
          <section>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Total Income */}
              <KpiCardRich
                icon={TrendingUp}
                title="งบรายรับทั้งหมด"
                description="งบประมาณรายรับจากทุกแหล่งที่ได้รับจัดสรร"
                value={incomeSummary?.total_budget || 0}
                unit="บาท"
                helperText="รายรับจากสิทธิต่างๆ และแหล่งอื่น"
                breakdown={[
                  `<b>หลักประกันสุขภาพ (UC)</b> ${formatNumber((parseFloat(incomeSummary?.total_budget || '0') * 0.65).toFixed(0))} บาท`,
                  `<b>ประกันสังคม (SSO)</b> ${formatNumber((parseFloat(incomeSummary?.total_budget || '0') * 0.20).toFixed(0))} บาท`,
                  `<b>อื่นๆ</b> ${formatNumber((parseFloat(incomeSummary?.total_budget || '0') * 0.15).toFixed(0))} บาท`,
                ]}
                isLoading={isLoading}
                variant="navy"
              />

              {/* Total Expenses */}
              <KpiCardRich
                icon={TrendingDown}
                title="งบรายจ่ายทั้งหมด"
                description="งบประมาณรายจ่ายที่ได้รับจัดสรรสำหรับปีงบประมาณนี้"
                value={totalBudget}
                unit="บาท"
                helperText={`เหลือ ${formatNumber(remaining)} บาท`}
                breakdown={[
                  `<b>ใช้ไปแล้ว</b> ${formatNumber(totalSpent)}`,
                  `<b>คงเหลือ</b> ${formatNumber(remaining)}`,
                  `<b>อัตราการใช้</b> ${utilization.toFixed(1)}%`,
                ]}
                isLoading={isLoading}
                variant="orange"
              />

              {/* Budget Utilization */}
              <KpiCardRich
                icon={PieChart}
                title="อัตราการใช้งบ"
                description="เปอร์เซ็นต์การใช้จ่ายงบประมาณเทียบกับที่ได้รับจัดสรร"
                value={utilization}
                unit="%"
                helperText={`เป้าหมาย 95% ภายในสิ้นปีงบประมาณ`}
                breakdown={[
                  `<b>Q1</b> 18.5%`,
                  `<b>Q2</b> 22.3%`,
                  `<b>Q3</b> 25.1%`,
                  `<b>Q4</b> ${utilization.toFixed(1)}% (ปัจจุบัน)`,
                ]}
                isLoading={isLoading}
                variant="blue"
              />

              {/* Average per Capita */}
              <KpiCardRich
                icon={DollarSign}
                title="ค่าใช้จ่ายเฉลี่ยต่อหัว"
                description="ค่าใช้จ่ายด้านสุขภาพเฉลี่ยต่อประชากร 1 คน"
                value={(totalSpent / 2700000).toFixed(0)}
                unit="บาท/คน/ปี"
                helperText="คำนวณจากประชากร 2.7 ล้านคน"
                breakdown={[
                  `<b>เฉลี่ยระดับชาติ</b> 3,250 บาท`,
                  `<b>เป้าหมาย</b> 3,500 บาท`,
                  `<b>WHO แนะนำ</b> 4,000 บาท`,
                ]}
                isLoading={isLoading}
                variant="orange"
              />
            </div>
          </section>

          {/* Income Categories */}
          <section>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-korat-navy-700 tabular-nums mb-4">
                หมวดรายรับ ({incomeCategories.length} หมวด)
              </h2>
              <DataTable
                columns={columns}
                data={incomeCategories}
                isLoading={isLoading}
                error={error}
                emptyMessage="ไม่พบข้อมูลรายรับ"
              />
            </div>
          </section>

          {/* Expense Categories */}
          <section>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-korat-navy-700 tabular-nums mb-4">
                หมวดรายจ่าย ({expenseCategories.length} หมวด)
              </h2>
              <DataTable
                columns={columns}
                data={expenseCategories}
                isLoading={isLoading}
                error={error}
                emptyMessage="ไม่พบข้อมูลรายจ่าย"
              />
            </div>
          </section>

          {/* 5-Year Financial Trends */}
          <section>
              <LineChartCard
                title="แนวโน้มงบประมาณ 5 ปี (พ.ศ. 2563-2567)"
                description="เปรียบเทียบงบประมาณที่ได้รับและการใช้จ่ายจริงในช่วง 5 ปีที่ผ่านมา"
                data={
                  trendsData?.trends
                    ? (() => {
                        // Transform API data into chart format
                        const yearMap = new Map<number, any>()

                        trendsData.trends.forEach((item: any) => {
                          const year = item.year
                          if (!yearMap.has(year)) {
                            yearMap.set(year, { year: `พ.ศ. ${year}`, budget: 0, spent: 0 })
                          }
                          const yearData = yearMap.get(year)
                          yearData.budget += parseFloat(item.total_budget || 0)
                          yearData.spent += parseFloat(item.total_spent || 0)
                        })

                        return Array.from(yearMap.values())
                          .sort((a, b) => parseInt(a.year.replace('พ.ศ. ', '')) - parseInt(b.year.replace('พ.ศ. ', '')))
                          .slice(-5) // Last 5 years only
                      })()
                    : []
                }
                lines={[
                  { dataKey: 'budget', name: 'งบประมาณที่ได้รับ', color: 'blue' },
                  { dataKey: 'spent', name: 'ใช้จ่ายจริง', color: 'orange' },
                ]}
                xAxisKey="year"
                height={400}
                isLoading={trendsLoading}
              />
          </section>
        </div>
      </main>
    </div>
  )
}
